import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Space,
    Group,
    Title,
    Grid,
    ActionIcon,
    Button,
    TextInput,
    Textarea,
    Card,
    Stack,
    Text,
    Box,
    Paper,
    CloseButton,
    Modal,
    ThemeIcon,
    Center,
    Badge,
    Switch,
} from '@mantine/core';
import {
    IconArrowLeft,
    IconCheck,
    IconEdit,
    IconGripVertical,
    IconPlus,
    IconReplace,
    IconFileTypePdf,
} from '@tabler/icons-react';
import { useListState } from '@mantine/hooks';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { showNotification } from '@mantine/notifications';
import { useFirestore } from '../../helpers/firebaseContext';
import {
    KYCPage,
    KYCProduct,
    ItemReference,
    PageOverride,
    SkipKYCPage,
    DisplayItem,
    PageVariablesOverride,
} from '../../types';

import { getVersionCode } from '../kyc/utils/versionCode';
import { TextFieldEditor } from './components/TextFieldEditorModal';

// ==================== CONSTANTS ====================

/**
 * Firestore collection paths
 */
const FIRESTORE_PATHS = {
    CONFIG: 'config',
    KYC_CONFIG: 'kyc_configuration',
    KYC_PAGES: 'kyc_pages',
    KYC_PRODUCTS: 'kyc_products',
} as const;

/**
 * Item type badges configuration
 */
const ITEM_BADGES = {
    PAGE: { color: 'blue', label: 'Page' },
    PRODUCT: { color: 'green', label: 'Product' },
} as const;

/**
 * Default product structure for new products
 */
const DEFAULT_PRODUCT: KYCProduct = {
    id: '',
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    pages: [],
    pdf_page_index: [],
    version: '', // Auto-generate version code on creation
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Extracts product ID from URL path
 * @param {string} pathname - Current URL pathname
 * @returns {string | undefined} Extracted product ID
 */
const extractProductIdFromUrl = (pathname: string): string | undefined => {
    const urlParts = pathname.split('/');
    const lastPart = urlParts[urlParts.length - 1];
    return lastPart !== 'add_product' ? lastPart : undefined;
};

const getCurrentRoutePath = (pathname: string, search: string, hash: string): string =>
    `${pathname}${search}${hash}`;

const buildPageEditPath = (pageId: string, returnTo: string): string =>
    `/kyc_configuration/add_page/${encodeURIComponent(pageId)}?returnTo=${encodeURIComponent(returnTo)}`;

/**
 * Validates product data before saving
 * @param {KYCProduct} productData - Product data to validate
 * @param {DisplayItem[]} selectedItems - Selected items array
 * @returns {{ isValid: boolean; error?: string }} Validation result
 */
const validateProductData = (
    productData: KYCProduct,
    selectedItems: DisplayItem[]
): { isValid: boolean; error?: string } => {
    if (!productData.id.trim()) {
        return { isValid: false, error: 'Product ID is required' };
    }

    if (!productData.title.trim()) {
        return { isValid: false, error: 'Product Title is required' };
    }

    if (selectedItems.length === 0) {
        return { isValid: false, error: 'At least one item (page or product) must be added' };
    }

    return { isValid: true };
};

/**
 * Checks if a product reference would create a circular dependency
 * @param {string} productId - Product ID to check
 * @param {string} currentProductId - Current product ID
 * @returns {boolean} True if circular dependency detected
 */
const wouldCreateCircularDependency = (productId: string, currentProductId?: string): boolean => {
    return productId === currentProductId;
};

// ==================== CUSTOM HOOKS ====================

/**
 * Hook for managing product ID extraction and edit mode detection
 */
const useProductId = () => {
    const { productId } = useParams<{ productId?: string }>();
    const currentUrl = window.location.pathname;

    const actualProductId = useMemo(() => {
        const extractedId = extractProductIdFromUrl(currentUrl);
        return productId || extractedId;
    }, [productId, currentUrl]);

    const isEditMode = useMemo(() => {
        return actualProductId !== undefined && actualProductId !== 'new';
    }, [actualProductId]);

    return { actualProductId, isEditMode };
};

/**
 * Hook for loading KYC data from Firestore
 */
const useKYCData = (db: any) => {
    const [isLoading, setIsLoading] = useState(true);
    const [availablePages, setAvailablePages] = useState<KYCPage[]>([]);
    const [availableProducts, setAvailableProducts] = useState<KYCProduct[]>([]);

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);

            // Load KYC pages
            const pagesSnapshot = await getDocs(
                collection(db, FIRESTORE_PATHS.CONFIG, FIRESTORE_PATHS.KYC_CONFIG, FIRESTORE_PATHS.KYC_PAGES)
            );
            const pages: KYCPage[] = [];
            pagesSnapshot.forEach((doc) => {
                pages.push({ ...doc.data() } as KYCPage);
            });
            setAvailablePages(pages);

            // Load KYC products
            const productsSnapshot = await getDocs(
                collection(db, FIRESTORE_PATHS.CONFIG, FIRESTORE_PATHS.KYC_CONFIG, FIRESTORE_PATHS.KYC_PRODUCTS)
            );
            const products: KYCProduct[] = [];
            productsSnapshot.forEach((doc) => {
                products.push({ ...doc.data() } as KYCProduct);
            });
            setAvailableProducts(products);
        } catch (error) {
            console.error('Error loading data:', error);
            showNotification({
                color: 'red',
                title: 'Error',
                message: 'Failed to load data: ' + error,
                autoClose: true,
            });
        } finally {
            setIsLoading(false);
        }
    }, [db]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return { isLoading, availablePages, availableProducts, reloadData: loadData };
};

/**
 * Hook for managing product pages recursively
 */
const useProductPages = (availablePages: KYCPage[], availableProducts: KYCProduct[]) => {
    /**
     * Recursively retrieves all pages from a product, including nested products
     * @param {string} productId - The product ID to get pages from
     * @returns {KYCPage[]} Array of pages
     */
    const MAX_RECURSION_DEPTH = 10; // Configurable depth limit
    const getProductPages = useCallback(
        (
            productId: string,
            visitedProducts: Set<string> = new Set(),
            depth: number = 0
        ): KYCPage[] => {
            const stack: Array<{
                productId: string;
                visitedProducts: Set<string>;
                depth: number;
            }> = [{
                productId,
                visitedProducts: new Set(visitedProducts),
                depth
            }];
            const pages: KYCPage[] = [];
            while (stack.length > 0) {
                const { productId: currentProductId, visitedProducts: currentVisited, depth: currentDepth } = stack.pop()!;
                // Check recursion depth limit
                if (currentDepth >= MAX_RECURSION_DEPTH) {
                    console.error(
                        `Maximum recursion depth (${MAX_RECURSION_DEPTH}) reached for product "${currentProductId}". Possible circular dependency or overly nested structure.`
                    );
                    continue;
                }
                // Check circular dependency
                if (currentVisited.has(currentProductId)) {
                    console.warn(
                        `Circular dependency detected: Product "${currentProductId}" references itself at depth ${currentDepth}. Skipping.`
                    );
                    continue;
                }
                const product = availableProducts.find((p) => p.id === currentProductId);
                if (!product?.pages) continue;
                const newVisitedProducts = new Set(currentVisited);
                newVisitedProducts.add(currentProductId);
                product.pages.forEach((ref) => {
                    if (typeof ref === 'string') {
                        const page = availablePages.find((p) => p.id === ref);
                        if (page) pages.push(page);
                    } else if (ref.type === 'page') {
                        const page = availablePages.find((p) => p.id === ref.id);
                        if (page) pages.push(page);
                    } else if (ref.type === 'product') {
                        // Push nested product onto stack with incremented depth
                        stack.push({
                            productId: ref.id,
                            visitedProducts: newVisitedProducts,
                            depth: currentDepth + 1
                        });
                    }
                });
            }

            return pages;
        },
        [availablePages, availableProducts]
    );

    return { getProductPages };
};

/**
 * Hook for managing modal states
 */
const useModalStates = () => {
    const [showItemSelector, setShowItemSelector] = useState(false);
    const [showOverrideSelector, setShowOverrideSelector] = useState(false);
    const [selectorType, setSelectorType] = useState<'page' | 'product'>('page');

    const openItemSelector = useCallback((type: 'page' | 'product') => {
        setSelectorType(type);
        setShowItemSelector(true);
    }, []);

    const closeItemSelector = useCallback(() => {
        setShowItemSelector(false);
    }, []);

    const closeOverrideSelector = useCallback(() => {
        setShowOverrideSelector(false);
    }, []);

    return {
        showItemSelector,
        showOverrideSelector,
        selectorType,
        openItemSelector,
        closeItemSelector,
        setShowOverrideSelector,
        closeOverrideSelector,
    };
};

// ==================== HELPER COMPONENTS ====================

/**
 * Empty state component for selected items list
 */
const EmptyItemsState: React.FC = React.memo(() => (
    <Paper p="md" withBorder style={{ backgroundColor: '#f8f9fa' }}>
        <Center>
            <Text size="sm" c="dimmed">
                No items selected. Click "Add Page" or "Add Product" to get started.
            </Text>
        </Center>
    </Paper>
));

EmptyItemsState.displayName = 'EmptyItemsState';

/**
 * Item badge component for displaying item type
 */
const ItemBadge: React.FC<{ type: 'page' | 'product'; size?: 'xs' | 'sm' | 'md'; variant?: string }> = React.memo(
    ({ type, size = 'xs', variant = 'filled' }) => {
        const config = type === 'page' ? ITEM_BADGES.PAGE : ITEM_BADGES.PRODUCT;
        return (
            <Badge size={size} color={config.color} variant={variant}>
                {config.label}
            </Badge>
        );
    }
);

ItemBadge.displayName = 'ItemBadge';

// ==================== MAIN COMPONENT ====================

/**
 * AddProductForm Component
 * 
 * A comprehensive form for creating and editing KYC (Know Your Customer) products.
 * Supports complex product configurations including:
 * - Adding pages and nested products
 * - Page overrides for customization
 * - Section hiding for conditional display
 * - Skip KYC for pages and child pages
 * - PDF page index management
 * - Drag-and-drop reordering
 * - Bilingual support (English/Arabic)
 * 
 * @component
 * @returns {JSX.Element} The KYC Product form with advanced configuration options
 */
const AddProductForm: React.FC = () => {
    // ==================== Hooks ====================
    const navigate = useNavigate();
    const location = useLocation();
    const db = useFirestore();
    const { actualProductId, isEditMode } = useProductId();
    const { isLoading, availablePages, availableProducts } = useKYCData(db);
    const { getProductPages } = useProductPages(availablePages, availableProducts);
    const modalStates = useModalStates();

    // ==================== State Management ====================
    const [isSaving, setIsSaving] = useState(false);
    const [productData, setProductData] = useState<KYCProduct>(DEFAULT_PRODUCT);
    const [selectedItems, selectedItemsHandlers] = useListState<DisplayItem>([]);
    const [selectedPageForOverride, setSelectedPageForOverride] = useState<{
        pageId: string;
        parentIndex: number;
    } | null>(null);

    // PDF Page Index modal state
    const [showPdfIndexModal, setShowPdfIndexModal] = useState(false);
    const [pdfPageIndex, pdfPageIndexHandlers] = useListState<string>([]);
    const [tempPdfPageIndex, setTempPdfPageIndex] = useState<string[]>([]);


    // Configuration modal state
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [configItemIndex, setConfigItemIndex] = useState<number>(-1);

    const returnToPath = useMemo(
        () => getCurrentRoutePath(location.pathname, location.search, location.hash),
        [location.hash, location.pathname, location.search]
    );

    const handleEditPage = useCallback(
        (pageId: string) => {
            navigate(buildPageEditPath(pageId, returnToPath));
        },
        [navigate, returnToPath]
    );

    const handleOpenConfigModal = useCallback((index: number) => {
        setConfigItemIndex(index);
        setShowConfigModal(true);
    }, []);

    const handleCloseConfigModal = useCallback(() => {
        setShowConfigModal(false);
        setConfigItemIndex(-1);
    }, []);

    // ==================== Computed Values ====================

    /**
     * Filters available pages excluding already selected ones
     */
    const availablePagesToAdd = useMemo(() => {
        return availablePages.filter(
            (page) => !selectedItems.some((item) => item.id === page.id && item.type === 'page')
        );
    }, [availablePages, selectedItems]);

    /**
     * Filters available products excluding already selected ones and current product
     */
    const availableProductsToAdd = useMemo(() => {
        return availableProducts.filter(
            (product) =>
                !selectedItems.some((item) => item.id === product.id && item.type === 'product') &&
                product.id !== actualProductId
        );
    }, [availableProducts, selectedItems, actualProductId]);

    /**
     * Gets all page IDs from selected items (direct pages + child pages from products)
     */
    const allPageIds = useMemo(() => {
        const pageIds: string[] = [];
        selectedItems.forEach((item) => {
            if (item.type === 'page') {
                pageIds.push(item.id);
            } else if (item.type === 'product' && item.childPages) {
                item.childPages.forEach((childPage) => {
                    pageIds.push(childPage.id);
                });
            }
        });
        return pageIds;
    }, [selectedItems]);

    /**
     * Gets all available pages for PDF index (only pages in selectedItems)
     */
    const pdfIndexAvailablePages = useMemo(() => {
        return availablePages.filter((page) => allPageIds.includes(page.id));
    }, [availablePages, allPageIds]);

    // ==================== Helper Functions ====================

    /**
     * Gets the override page ID for a specific page within an item
     */
    const getOverrideForPage = useCallback(
        (pageId: string, itemIndex: number): string | undefined => {
            const item = selectedItems[itemIndex];
            const overrides = item.override_pages || [];
            const override = overrides.find((o) => o.page_id === pageId);
            return override?.override_page_id;
        },
        [selectedItems]
    );

    /**
     * Gets hidden section IDs for a specific page within an item
     */
    const getHiddenSectionsForPage = useCallback(
        (pageId: string, itemIndex: number): string[] => {
            const item = selectedItems[itemIndex];
            const hideSections = item.hide_sections || [];
            const hideSection = hideSections.find((h) => h.page_id === pageId);
            return hideSection?.section_id || [];
        },
        [selectedItems]
    );

    /**
     * Gets the is_skip_kyc status for a specific child page
     */
    const getSkipKycForChildPage = useCallback(
        (pageId: string, itemIndex: number): boolean => {
            const item = selectedItems[itemIndex];
            const skipPages = item.skip_pages || [];
            const skipPage = skipPages.find((s) => s.page_id === pageId);
            return skipPage?.is_skip_kyc || false;
        },
        [selectedItems]
    );

    /**
     * Enriches a reference with display data
     */
    const enrichReference = useCallback(
        (ref: ItemReference | string): DisplayItem | null => {
            if (typeof ref === 'string') {
                // Legacy string reference
                const page = availablePages.find((p) => p.id === ref);
                return page
                    ? {
                        id: page.id,
                        type: 'page' as const,
                        title: page.title,
                        title_ar: page.title_ar,
                        pageVariables: page.pageVariables || {},
                        is_skip_kyc: false,
                    }
                    : null;
            }

            if (ref.type === 'page') {
                // Page reference with potential overrides
                const page = availablePages.find((p) => p.id === ref.id);
                return page
                    ? {
                        id: page.id,
                        type: 'page' as const,
                        title: page.title,
                        title_ar: page.title_ar,
                        override_pages: ref.override_pages,
                        hide_sections: ref.hide_sections,
                        is_skip_kyc: ref.is_skip_kyc || false,
                        product_title: ref.product_title,
                        product_title_ar: ref.product_title_ar,
                        product_description: ref.product_description,
                        product_description_ar: ref.product_description_ar,
                        header_left_image: ref.header_left_image,
                        header_right_image: ref.header_right_image,
                        footer_left_image: ref.footer_left_image,
                        footer_text: ref.footer_text,
                        isSeparatePage: ref.isSeparatePage || false,
                        hideHeaderFooter: ref.hideHeaderFooter || false,
                        pageVariables: {
                            ...(page.pageVariables || {}),
                            ...(ref.pageVariables || {}),
                        },
                    }
                    : null;
            } else {
                // Product reference with child pages
                const product = availableProducts.find((p) => p.id === ref.id);
                if (product) {
                    const childPages = getProductPages(product.id);

                    // Apply pageVariablesOverride to child pages with MERGE
                    const enrichedChildPages = childPages.map((childPage) => {
                        const override = ref.pageVariablesOverride?.find(
                            (pvo) => pvo.page_id === childPage.id
                        );

                        return {
                            ...childPage,
                            // MERGE: page defaults + overrides (overrides take priority)
                            pageVariables: {
                                ...(childPage.pageVariables || {}),
                                ...(override?.pageVariables || {}),
                            },
                        };
                    });

                    return {
                        id: product.id,
                        type: 'product' as const,
                        title: product.title,
                        title_ar: product.title_ar,
                        description: product.description,
                        description_ar: product.description_ar,
                        product_title: ref.product_title,
                        product_title_ar: ref.product_title_ar,
                        product_description: ref.product_description,
                        product_description_ar: ref.product_description_ar,
                        header_left_image: ref.header_left_image,
                        header_right_image: ref.header_right_image,
                        footer_left_image: ref.footer_left_image,
                        footer_text: ref.footer_text,
                        childPages: enrichedChildPages,
                        override_pages: ref.override_pages,
                        hide_sections: ref.hide_sections,
                        isSeparatePage: ref.isSeparatePage || false,
                        hideHeaderFooter: ref.hideHeaderFooter || false,
                        skip_pages: ref.skip_pages,
                        is_skip_kyc: ref.is_skip_kyc || false,
                        pageVariables: {},
                    };
                }
                return null;
            }
        },
        [availablePages, availableProducts, getProductPages]
    );

    // ==================== Effects ====================

    /**
     * Load existing product data in edit mode
     */
    useEffect(() => {
        if (
            actualProductId &&
            actualProductId !== 'new' &&
            availableProducts.length > 0 &&
            availablePages.length > 0
        ) {
            const existingProduct = availableProducts.find((p) => p.id === actualProductId);
            if (existingProduct) {
                setProductData({ ...existingProduct });

                // Enrich page/product references with display data
                if (existingProduct.pages && Array.isArray(existingProduct.pages)) {
                    const enrichedItems = existingProduct.pages
                        .map(enrichReference)
                        .filter(Boolean) as DisplayItem[];

                    selectedItemsHandlers.setState(enrichedItems);
                }

                // Load PDF page index
                if (existingProduct.pdf_page_index && Array.isArray(existingProduct.pdf_page_index)) {
                    pdfPageIndexHandlers.setState(existingProduct.pdf_page_index);
                }
            }
        } else if (!actualProductId || actualProductId === 'new') {
            // Reset form for new product
            setProductData(DEFAULT_PRODUCT);
            selectedItemsHandlers.setState([]);
            pdfPageIndexHandlers.setState([]);
        }
    }, [actualProductId, availableProducts, availablePages, enrichReference]);

    /**
     * Sync selected items to product data
     */
    useEffect(() => {
        const references: ItemReference[] = selectedItems.map((item) => {
            const ref: ItemReference = {
                id: item.id,
                type: item.type,
            };

            if (item.product_title) ref.product_title = item.product_title;
            if (item.product_title_ar) ref.product_title_ar = item.product_title_ar;
            if (item.product_description) ref.product_description = item.product_description;
            if (item.product_description_ar) ref.product_description_ar = item.product_description_ar;
            if (item.isSeparatePage !== undefined) ref.isSeparatePage = item.isSeparatePage;
            if (item.hideHeaderFooter !== undefined) ref.hideHeaderFooter = item.hideHeaderFooter;

            if (item.header_left_image) ref.header_left_image = item.header_left_image;
            if (item.header_right_image) ref.header_right_image = item.header_right_image;
            if (item.footer_left_image) ref.footer_left_image = item.footer_left_image;
            if (item.footer_text) ref.footer_text = item.footer_text;

            // Add is_skip_kyc if it exists
            if (item.is_skip_kyc) {
                ref.is_skip_kyc = item.is_skip_kyc;
            }

            if (item.override_pages && item.override_pages.length > 0) {
                ref.override_pages = item.override_pages;
            }
            if (item.hide_sections && item.hide_sections.length > 0) {
                ref.hide_sections = item.hide_sections;
            }
            if (item.skip_pages && item.skip_pages.length > 0) {
                ref.skip_pages = item.skip_pages;
            }

            // For direct pages - save pageVariables directly
            if (item.type === 'page' && item.pageVariables && Object.keys(item.pageVariables).length > 0) {
                ref.pageVariables = item.pageVariables;
            }

            // For products - save child page variables as pageVariablesOverride array
            if (item.type === 'product' && item.childPages) {
                const pageVarsOverrides: PageVariablesOverride[] = [];

                item.childPages.forEach((childPage) => {
                    if (childPage.pageVariables && Object.keys(childPage.pageVariables).length > 0) {
                        pageVarsOverrides.push({
                            page_id: childPage.id,
                            pageVariables: childPage.pageVariables,
                        });
                    }
                });

                if (pageVarsOverrides.length > 0) {
                    ref.pageVariablesOverride = pageVarsOverrides;
                }
            }

            return ref;
        });
        setProductData((prev) => ({ ...prev, pages: references }));
    }, [selectedItems]);

    /**
 * Auto-update PDF index when items change
 */
    useEffect(() => {
        // When selectedItems change, add new page IDs to PDF index (if not already present)
        const newPageIds = allPageIds.filter((pageId) => !pdfPageIndex.includes(pageId));
        if (newPageIds.length > 0) {
            const updatedIndex = [...pdfPageIndex, ...newPageIds];
            pdfPageIndexHandlers.setState(updatedIndex);
            return;
        }

        // Remove page IDs from PDF index if they're no longer in selectedItems
        const validPageIds = pdfPageIndex.filter((pageId) => allPageIds.includes(pageId));
        if (validPageIds.length !== pdfPageIndex.length) {
            pdfPageIndexHandlers.setState(validPageIds);
        }
    }, [allPageIds, pdfPageIndex, pdfPageIndexHandlers]);

    /**
     * Sync PDF page index to product data
     */
    useEffect(() => {
        setProductData((prev) => ({ ...prev, pdf_page_index: pdfPageIndex }));
    }, [pdfPageIndex]);

    // ==================== Form Handlers ====================

    /**
     * Handles product ID changes
     */
    const handleIdChange = useCallback((value: string) => {
        setProductData((prev) => ({ ...prev, id: value }));
    }, []);

    /**
     * Handles title changes and auto-generates ID in create mode
     */
    const handleTitleChange = useCallback(
        (value: string) => {
            setProductData((prev) => ({
                ...prev,
                title: value,
            }));
        },
        []
    );

    /**
     * Handles Arabic title changes
     */
    const handleTitleArChange = useCallback((value: string) => {
        setProductData((prev) => ({ ...prev, title_ar: value }));
    }, []);

    /**
     * Handles description changes
     */
    const handleDescriptionChange = useCallback((value: string) => {
        setProductData((prev) => ({ ...prev, description: value }));
    }, []);

    /**
     * Handles Arabic description changes
     */
    const handleDescriptionArChange = useCallback((value: string) => {
        setProductData((prev) => ({ ...prev, description_ar: value }));
    }, []);

    // ==================== Item Management ====================

    /**
     * Adds a page to the selected items list
     */
    const handleAddPage = useCallback(
        (pageId: string) => {
            const page = availablePages.find((p) => p.id === pageId);
            if (page && !selectedItems.some((p) => p.id === pageId && p.type === 'page')) {
                selectedItemsHandlers.append({
                    id: page.id,
                    type: 'page',
                    title: page.title,
                    title_ar: page.title_ar,
                    is_skip_kyc: false,
                    pageVariables: page.pageVariables || {},
                });
            }
            modalStates.closeItemSelector();
        },
        [availablePages, selectedItems, selectedItemsHandlers, modalStates]
    );

    /**
     * Adds a product to the selected items list
     */
    const handleAddProduct = useCallback(
        (productId: string) => {
            const product = availableProducts.find((p) => p.id === productId);
            if (!product || selectedItems.some((p) => p.id === productId && p.type === 'product')) {
                return;
            }

            // Prevent self-reference
            if (wouldCreateCircularDependency(product.id, actualProductId)) {
                showNotification({
                    color: 'yellow',
                    title: 'Warning',
                    message: 'Cannot add a product to itself',
                    autoClose: true,
                });
                return;
            }

            const childPages = getProductPages(product.id);
            selectedItemsHandlers.append({
                id: product.id,
                type: 'product',
                title: product.title,
                title_ar: product.title_ar,
                description: product.description,
                description_ar: product.description_ar,
                childPages: childPages,
            });

            modalStates.closeItemSelector();
        },
        [availableProducts, selectedItems, actualProductId, getProductPages, selectedItemsHandlers, modalStates]
    );

    /**
     * Removes an item from the selected items list
     */
    const handleRemoveItem = useCallback(
        (index: number) => {
            selectedItemsHandlers.remove(index);
        },
        [selectedItemsHandlers]
    );

    /**
     * Handles item reordering
     */
    const handleReorderItem = useCallback(
        (from: number, to: number) => {
            selectedItemsHandlers.reorder({ from, to });
        },
        [selectedItemsHandlers]
    );

    // ==================== Override Management ====================

    /**
     * Opens the override selector modal
     */
    const handleOpenOverrideSelector = useCallback(
        (pageId: string, parentIndex: number) => {
            setSelectedPageForOverride({ pageId, parentIndex });
            modalStates.setShowOverrideSelector(true);
        },
        [modalStates]
    );

    /**
     * Handles page override selection
     */
    const handleOverridePage = useCallback(
        (newPageId: string) => {
            if (!selectedPageForOverride) return;

            const { pageId, parentIndex } = selectedPageForOverride;
            const item = selectedItems[parentIndex];
            const overrides = item.override_pages || [];
            const existingIndex = overrides.findIndex((o) => o.page_id === pageId);

            let newOverrides: PageOverride[];
            if (existingIndex >= 0) {
                // Update existing override
                newOverrides = [...overrides];
                newOverrides[existingIndex] = {
                    page_id: pageId,
                    override_page_id: newPageId,
                };
            } else {
                // Add new override
                newOverrides = [
                    ...overrides,
                    {
                        page_id: pageId,
                        override_page_id: newPageId,
                    },
                ];
            }

            selectedItemsHandlers.setItemProp(parentIndex, 'override_pages', newOverrides);
            modalStates.closeOverrideSelector();
            setSelectedPageForOverride(null);

            showNotification({
                color: 'teal',
                title: 'Page Override Set',
                message: 'Page override configured successfully',
                autoClose: 2000,
            });
        },
        [selectedPageForOverride, selectedItems, selectedItemsHandlers, modalStates]
    );

    /**
     * Removes a page override
     */
    const handleRemoveOverride = useCallback(
        (pageId: string, parentIndex: number) => {
            const item = selectedItems[parentIndex];
            const overrides = item.override_pages || [];
            const newOverrides = overrides.filter((o) => o.page_id !== pageId);
            selectedItemsHandlers.setItemProp(parentIndex, 'override_pages', newOverrides);
        },
        [selectedItems, selectedItemsHandlers]
    );

    // ==================== Skip KYC Management ====================

    /**
     * Updates is_skip_kyc for a child page
     */
    const handleUpdateChildPageSkipKyc = useCallback(
        (parentIndex: number, childPageId: string, value: boolean) => {
            const item = selectedItems[parentIndex];

            if (item.type !== 'product') return;

            // Get current skip_pages
            const skipPages = item.skip_pages || [];

            // Find if there's already an entry for this child page
            const existingIndex = skipPages.findIndex((s) => s.page_id === childPageId);

            let newSkipPages: SkipKYCPage[];

            if (value) {
                // Add or update skip entry
                if (existingIndex >= 0) {
                    newSkipPages = [...skipPages];
                    newSkipPages[existingIndex] = {
                        page_id: childPageId,
                        is_skip_kyc: true,
                    };
                } else {
                    newSkipPages = [
                        ...skipPages,
                        {
                            page_id: childPageId,
                            is_skip_kyc: true,
                        },
                    ];
                }
            } else {
                // Remove skip entry when false
                newSkipPages = skipPages.filter((s) => s.page_id !== childPageId);
            }

            selectedItemsHandlers.setItemProp(parentIndex, 'skip_pages', newSkipPages);
        },
        [selectedItems, selectedItemsHandlers]
    );

    // ==================== PDF Page Index Management ====================

    /**
     * Opens the PDF page index modal
     */
    const handleOpenPdfIndexModal = useCallback(() => {
        setTempPdfPageIndex([...pdfPageIndex]); // Copy current to temp
        setShowPdfIndexModal(true);
    }, [pdfPageIndex]);

    /**
     * Closes the PDF page index modal
     */
    const handleClosePdfIndexModal = useCallback(() => {
        setShowPdfIndexModal(false);
        setTempPdfPageIndex([]); // Clear temp on cancel
    }, []);

    /**
     * Handles PDF page index reordering
     */
    const handleReorderPdfIndex = useCallback(
        (from: number, to: number) => {
            const newIndex = [...tempPdfPageIndex];
            const [movedItem] = newIndex.splice(from, 1);
            newIndex.splice(to, 0, movedItem);
            setTempPdfPageIndex(newIndex);
        },
        [tempPdfPageIndex]
    );

    /**
     * Saves PDF page order index
     */
    const handleSavePdfIndex = useCallback(() => {
        pdfPageIndexHandlers.setState(tempPdfPageIndex); // Apply temp to real
        showNotification({
            color: 'teal',
            title: 'Success',
            message: 'PDF page order updated successfully',
            autoClose: 2000,
        });
        setShowPdfIndexModal(false);
        setTempPdfPageIndex([]); // Clear temp
    }, [tempPdfPageIndex, pdfPageIndexHandlers]);

    // ==================== Save Handler ====================

    /**
     * Saves the product to Firestore
     */
    const handleSave = useCallback(async () => {
        const validation = validateProductData(productData, selectedItems);

        if (!validation.isValid) {
            showNotification({
                color: 'red',
                title: 'Validation Error',
                message: validation.error,
                autoClose: true,
            });
            return;
        }

        setIsSaving(true);
        try {
            // Ensure parent document exists
            const parentRef = doc(db, FIRESTORE_PATHS.CONFIG, FIRESTORE_PATHS.KYC_CONFIG);
            await setDoc(parentRef, { initialized: true }, { merge: true });

            // Save product
            const productRef = doc(
                db,
                FIRESTORE_PATHS.CONFIG,
                FIRESTORE_PATHS.KYC_CONFIG,
                FIRESTORE_PATHS.KYC_PRODUCTS,
                productData.id
            );
            // Update version for concurrency control without mutating state
            const updatedProductData: KYCProduct = {
                ...productData,
                version: getVersionCode(),
            };
            await setDoc(productRef, updatedProductData);

            showNotification({
                color: 'teal',
                title: 'Success',
                message: isEditMode ? 'Product updated successfully!' : 'Product created successfully!',
                autoClose: true,
            });

            // Navigate back after short delay
            setTimeout(() => {
                navigate('/kyc_configuration');
            }, 1500);
        } catch (error) {
            console.error('Error saving product:', error);
            showNotification({
                color: 'red',
                title: 'Error',
                message: 'Failed to save product: ' + error,
                autoClose: 10000,
            });
        } finally {
            setIsSaving(false);
        }
    }, [productData, selectedItems, db, isEditMode, navigate]);

    // ==================== Drag and Drop Handlers ====================

    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        e.dataTransfer.setData('text/plain', index.toString());
        (e.currentTarget as HTMLElement).style.opacity = '0.5';
    }, []);

    const handleDragEnd = useCallback((e: React.DragEvent) => {
        (e.currentTarget as HTMLElement).style.opacity = '1';
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).style.backgroundColor = '#e3f2fd';
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = '';
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent, targetIndex: number) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).style.backgroundColor = '';
            const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
            if (draggedIndex !== targetIndex) {
                handleReorderItem(draggedIndex, targetIndex);
            }
        },
        [handleReorderItem]
    );

    // PDF Index Drag and Drop
    const handlePdfDragStart = useCallback((e: React.DragEvent, index: number) => {
        e.dataTransfer.setData('text/plain', index.toString());
        (e.currentTarget as HTMLElement).style.opacity = '0.5';
    }, []);

    const handlePdfDragEnd = useCallback((e: React.DragEvent) => {
        (e.currentTarget as HTMLElement).style.opacity = '1';
    }, []);

    const handlePdfDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).style.backgroundColor = '#e3f2fd';
    }, []);

    const handlePdfDragLeave = useCallback((e: React.DragEvent) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = '';
    }, []);

    const handlePdfDrop = useCallback(
        (e: React.DragEvent, targetIndex: number) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).style.backgroundColor = '';
            const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
            if (draggedIndex !== targetIndex) {
                const newIndex = [...tempPdfPageIndex];
                const [movedItem] = newIndex.splice(draggedIndex, 1);
                newIndex.splice(targetIndex, 0, movedItem);
                setTempPdfPageIndex(newIndex);
            }
        },
        [tempPdfPageIndex]
    );


    // ==================== Page Variables Management ====================

    /**
     * State for page variables editor modal
     */
    interface PageVariableEditor {
        isOpen: boolean;
        pageId: string;
        itemIndex: number;
        variables: Record<string, string>;
        isChildPage: boolean;
    }

    // Add this state in the main component (around line 410, with other states)
    const [pageVariableEditor, setPageVariableEditor] = useState<PageVariableEditor>({
        isOpen: false,
        pageId: '',
        itemIndex: -1,
        variables: {},
        isChildPage: false,
    });

    /**
    * Opens the page variables editor modal
    */
    const handleOpenPageVariablesEditor = useCallback(
        (pageId: string, itemIndex: number, variables: Record<string, string>, isChildPage: boolean = false) => {
            setPageVariableEditor({
                isOpen: true,
                pageId,
                itemIndex,
                variables: { ...variables }, // Clone to avoid direct mutation
                isChildPage,
            });
        },
        []
    );
    /**
     * Closes the page variables editor modal
     */
    const handleClosePageVariablesEditor = useCallback(() => {
        setPageVariableEditor({
            isOpen: false,
            pageId: '',
            itemIndex: -1,
            variables: {},
            isChildPage: false,
        });
    }, []);

    /**
     * Updates a page variable value in the editor
     */
    const handleUpdatePageVariable = useCallback((key: string, value: string) => {
        setPageVariableEditor((prev) => ({
            ...prev,
            variables: {
                ...prev.variables,
                [key]: value,
            },
        }));
    }, []);

    /**
     * Saves updated page variables
     */
    const handleSavePageVariables = useCallback(() => {
        const { pageId, itemIndex, variables, isChildPage } = pageVariableEditor;

        if (itemIndex < 0 || itemIndex >= selectedItems.length) return;

        const item = selectedItems[itemIndex];

        if (isChildPage && item.type === 'product') {
            // Update child page variables
            const updatedChildPages = item.childPages?.map((child) =>
                child.id === pageId
                    ? { ...child, pageVariables: variables }
                    : child
            );

            selectedItemsHandlers.setItemProp(itemIndex, 'childPages', updatedChildPages);
        } else if (!isChildPage && item.type === 'page') {
            // Update direct page variables
            selectedItemsHandlers.setItemProp(itemIndex, 'pageVariables', variables);
        }

        showNotification({
            color: 'teal',
            title: 'Success',
            message: 'Page variables updated successfully',
            autoClose: 2000,
        });

        handleClosePageVariablesEditor();
    }, [pageVariableEditor, selectedItems, selectedItemsHandlers, handleClosePageVariablesEditor]);

    // ==================== Page Variables Button Component ====================

    /**
     * Reusable button component for editing page variables
     */
    const PageVariablesButton: React.FC<{
        pageVariables?: Record<string, string>;
        onClick?: () => void;
    }> = React.memo(({ pageVariables, onClick }) => {
        if (!pageVariables || Object.keys(pageVariables).length === 0) {
            return null;
        }

        return (
            <Button
                size="xs"
                variant="light"
                color="blue"
                leftSection={<IconEdit size={12} />}
                onClick={onClick}
            >
                Edit Page Variables ({Object.keys(pageVariables).length})
            </Button>
        );
    });

    /**
     * Reusable button component for displaying PDF page index
     */
    const PdfIndexButton: React.FC<{
        pageId: string;
        pdfPageIndex: string[];
        onClick?: () => void;
    }> = React.memo(({ pageId, pdfPageIndex, onClick }) => {
        const pageIndex = pdfPageIndex.indexOf(pageId);
        const isInIndex = pageIndex !== -1;
        const displayIndex = isInIndex ? pageIndex + 1 : null;

        return (
            <Button
                size="xs"
                variant="light"
                color={isInIndex ? 'violet' : 'gray'}
                title={`PDF order: ${isInIndex ? displayIndex : 'Not included'}`}
                leftSection={<IconFileTypePdf size={12} />}
                onClick={onClick}
            >
                PDF #{isInIndex ? displayIndex : '?'}
            </Button>
        );
    });

    // ==================== Render ====================

    if (isLoading) {
        return (
            <main style={{ padding: '1rem 0' }}>
                <Text ta="center" size="lg">
                    Loading...
                </Text>
            </main>
        );
    }

    return (
        <main style={{ padding: '1rem 0' }}>

            {/* Page Variables Editor Modal */}
            <Modal
                opened={pageVariableEditor.isOpen}
                onClose={handleClosePageVariablesEditor}
                title="Edit Page Variables"
                size="md"
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Update the values for page variables. These will override the default values.
                    </Text>

                    {Object.keys(pageVariableEditor.variables).length === 0 ? (
                        <Text c="dimmed" ta="center">
                            No variables defined for this page.
                        </Text>
                    ) : (
                        Object.entries(pageVariableEditor.variables).map(([key, value]) => (
                            <TextInput
                                key={key}
                                label={key}
                                value={value}
                                onChange={(e) => handleUpdatePageVariable(key, e.target.value)}
                                placeholder={`Enter value for ${key}`}
                            />
                        ))
                    )}

                    <Group justify="flex-end" gap="sm" mt="md">
                        <Button
                            variant="default"
                            onClick={handleClosePageVariablesEditor}
                        >
                            Cancel
                        </Button>
                        <Button
                            leftSection={<IconCheck size={16} />}
                            onClick={handleSavePageVariables}
                        >
                            Save Changes
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* PDF Page Index Modal */}
            <Modal
                opened={showPdfIndexModal}
                onClose={handleClosePdfIndexModal}
                title="PDF Page order"
                size="lg"
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Drag and drop to reorder pages for PDF generation. Only pages included in this product are shown.
                    </Text>

                    {tempPdfPageIndex.length === 0 ? (
                        <Paper p="md" withBorder style={{ backgroundColor: '#f8f9fa' }}>
                            <Center>
                                <Text size="sm" c="dimmed">
                                    No pages available. Add pages or products to this product first.
                                </Text>
                            </Center>
                        </Paper>
                    ) : (
                        <div>
                            {tempPdfPageIndex.map((pageId, index) => {
                                const page = pdfIndexAvailablePages.find((p) => p.id === pageId);
                                if (!page) return null;

                                return (
                                    <Box key={`pdf-${pageId}-${index}`} mb="xs">
                                        <Paper
                                            p="sm"
                                            withBorder
                                            draggable
                                            onDragStart={(e) => handlePdfDragStart(e, index)}
                                            onDragEnd={handlePdfDragEnd}
                                            onDragOver={handlePdfDragOver}
                                            onDragLeave={handlePdfDragLeave}
                                            onDrop={(e) => handlePdfDrop(e, index)}
                                            style={{ cursor: 'move' }}
                                        >
                                            <Group justify="space-between">
                                                <Group>
                                                    <ThemeIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        color="gray"
                                                        style={{ cursor: 'grab' }}
                                                    >
                                                        <IconGripVertical size={14} />
                                                    </ThemeIcon>
                                                    <div>
                                                        <Group gap="xs">
                                                            <Text size="sm" fw={500}>
                                                                {index + 1}. {page.title}
                                                            </Text>
                                                            <ItemBadge type="page" />
                                                        </Group>
                                                        <Text size="xs" c="dimmed">
                                                            {page.title_ar} ({page.id})
                                                        </Text>
                                                    </div>
                                                </Group>
                                                <Group gap="xs">
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={() =>
                                                            index > 0 && handleReorderPdfIndex(index, index - 1)
                                                        }
                                                        disabled={index === 0}
                                                        aria-label="Move up"
                                                    >
                                                        ↑
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={() =>
                                                            index < tempPdfPageIndex.length - 1 &&
                                                            handleReorderPdfIndex(index, index + 1)
                                                        }
                                                        disabled={index === tempPdfPageIndex.length - 1}
                                                        aria-label="Move down"
                                                    >
                                                        ↓
                                                    </ActionIcon>
                                                </Group>
                                            </Group>
                                        </Paper>
                                    </Box>
                                );
                            })}
                        </div>
                    )}

                    <Group justify="flex-end" gap="sm" mt="md">
                        <Button variant="default" onClick={handleClosePdfIndexModal}>
                            Cancel
                        </Button>
                        <Button leftSection={<IconCheck size={16} />} onClick={handleSavePdfIndex}>
                            Save PDF order
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Configuration Modal */}
            <Modal
                opened={showConfigModal}
                onClose={handleCloseConfigModal}
                title={`Configuration — ${configItemIndex >= 0 ? selectedItems[configItemIndex]?.title : ''}`}
                size="xl"
            >
                {configItemIndex >= 0 && selectedItems[configItemIndex] && (() => {
                    const item = selectedItems[configItemIndex];
                    return (
                        <Stack gap="md">
                            <Text size="sm" c="dimmed">
                                Override title and description for PDF. Leave empty to use default.
                            </Text>

                            <Grid>
                                <Grid.Col span={6}>
                                    <TextInput
                                        label="Custom Title (EN)"
                                        placeholder={item.title}
                                        value={item.product_title ?? ''}
                                        onChange={(e) =>
                                            selectedItemsHandlers.setItemProp(
                                                configItemIndex,
                                                'product_title',
                                                e.target.value || undefined
                                            )
                                        }
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <TextInput
                                        label="Custom Title (AR)"
                                        placeholder={item.title_ar}
                                        value={item.product_title_ar ?? ''}
                                        onChange={(e) =>
                                            selectedItemsHandlers.setItemProp(
                                                configItemIndex,
                                                'product_title_ar',
                                                e.target.value || undefined
                                            )
                                        }
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Textarea
                                        label="Custom Description (EN)"
                                        placeholder={item.description ?? item.title}
                                        value={item.product_description ?? ''}
                                        minRows={2}
                                        onChange={(e) =>
                                            selectedItemsHandlers.setItemProp(
                                                configItemIndex,
                                                'product_description',
                                                e.target.value || undefined
                                            )
                                        }
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Textarea
                                        label="Custom Description (AR)"
                                        placeholder={item.description_ar ?? item.title_ar}
                                        value={item.product_description_ar ?? ''}
                                        minRows={2}
                                        onChange={(e) =>
                                            selectedItemsHandlers.setItemProp(
                                                configItemIndex,
                                                'product_description_ar',
                                                e.target.value || undefined
                                            )
                                        }
                                    />
                                </Grid.Col>
                            </Grid>

                            {/* ── Header Images (per-item) ── */}
                            <Box style={{ borderTop: '1px solid #dee2e6', paddingTop: 12 }}>
                                <Text size="sm" fw={600} mb={4}>Header/Footer Images (displayed when the main header/footer is hidden)</Text>
                                <Text size="xs" c="dimmed" mb="sm">PNG/JPG, max 200KB.</Text>
                                <Grid>
                                    <Grid.Col span={6}>
                                        <ImageUploadField
                                            label="Header Left Image"
                                            value={item.header_left_image}
                                            onChange={(val) =>
                                                selectedItemsHandlers.setItemProp(configItemIndex, 'header_left_image', val)
                                            }
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <ImageUploadField
                                            label="Header Right Image"
                                            value={item.header_right_image}
                                            onChange={(val) =>
                                                selectedItemsHandlers.setItemProp(configItemIndex, 'header_right_image', val)
                                            }
                                        />
                                    </Grid.Col>
                                </Grid>
                            </Box>

                            {/* ── Footer (per-item) ── */}
                            <Box style={{ borderTop: '1px solid #dee2e6', paddingTop: 12 }}>
                                <Text size="sm" fw={600} mb={4}>Footer</Text>
                                <Grid>
                                    <Grid.Col span={6}>
                                        <ImageUploadField
                                            label="Footer Left Image"
                                            value={item.footer_left_image}
                                            onChange={(val) =>
                                                selectedItemsHandlers.setItemProp(configItemIndex, 'footer_left_image', val)
                                            }
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <TextFieldEditor
                                            label="Content (English)"
                                            value={item.footer_text ?? ''}
                                            onChange={(value) => selectedItemsHandlers.setItemProp(configItemIndex, 'footer_text', value)}
                                            onRemove={() => selectedItemsHandlers.setItemProp(configItemIndex, 'footer_text', '')}
                                            language="en"
                                            placeholder="Click to add/edit rich text content"
                                        />
                                    </Grid.Col>
                                </Grid>
                            </Box>

                            {/* Show current overrides summary */}
                            {(item.product_title || item.product_title_ar ||
                                item.product_description || item.product_description_ar) && (
                                    <Paper p="xs" withBorder style={{ backgroundColor: '#f0f4ff' }}>
                                        <Text size="xs" fw={600} c="blue.7" mb={4}>Active Overrides:</Text>
                                        {item.product_title && (
                                            <Text size="xs">Title EN: {item.product_title}</Text>
                                        )}
                                        {item.product_title_ar && (
                                            <Text size="xs">Title AR: {item.product_title_ar}</Text>
                                        )}
                                        {item.product_description && (
                                            <Text size="xs">Desc EN: {item.product_description}</Text>
                                        )}
                                        {item.product_description_ar && (
                                            <Text size="xs">Desc AR: {item.product_description_ar}</Text>
                                        )}
                                    </Paper>
                                )}

                            <Group justify="space-between" mt="md">
                                <Group gap="xs">
                                    <Button
                                        variant="subtle"
                                        color="red"
                                        size="xs"
                                        onClick={() => {
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'product_title', undefined);
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'product_title_ar', undefined);
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'product_description', undefined);
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'product_description_ar', undefined);
                                        }}
                                    >
                                        Clear Title/Desc
                                    </Button>
                                    <Button
                                        variant="subtle"
                                        color="red"
                                        size="xs"
                                        onClick={() => {
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'header_left_image', undefined);
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'header_right_image', undefined);
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'footer_left_image', undefined);
                                            selectedItemsHandlers.setItemProp(configItemIndex, 'footer_text', undefined);
                                        }}
                                    >
                                        Clear Header/Footer
                                    </Button>
                                </Group>
                                <Button leftSection={<IconCheck size={16} />} onClick={handleCloseConfigModal}>
                                    Done
                                </Button>
                            </Group>
                        </Stack>
                    );
                })()}
            </Modal>

            {/* Header with back button and title */}
            <Group mb="xl">
                <ActionIcon
                    onClick={() => navigate('/kyc_configuration')}
                    aria-label="Back to KYC Configuration"
                >
                    <IconArrowLeft size="1.5rem" />
                </ActionIcon>
                <Title>{isEditMode ? 'Edit KYC Product' : 'Add KYC Product'}</Title>
            </Group>

            <Space h="xl" />

            {/* Main form card */}
            <Card withBorder p="lg">
                <Stack gap="md">
                    {/* Product ID field */}
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <TextInput
                                label="Product ID"
                                value={productData.id}
                                onChange={(e) => handleIdChange(e.target.value)}
                                placeholder="order_kyc"
                                required
                                disabled={isEditMode}
                                description={
                                    isEditMode ? 'Product ID cannot be changed' : 'Auto-generated from title'
                                }
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <TextInput
                                label="Product Version"
                                value={productData.version ?? ''}
                                placeholder="BH.2610.2145"
                                disabled={true}
                                description={
                                    'Auto-generated based on current time'
                                }
                            />
                        </Grid.Col>
                    </Grid>

                    {/* Title fields (English & Arabic) */}
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <TextInput
                                label="Title (English)"
                                value={productData.title}
                                onChange={(e) => handleTitleChange(e.target.value)}
                                placeholder="Order KYC"
                                required
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <TextInput
                                label="Title (Arabic)"
                                value={productData.title_ar}
                                onChange={(e) => handleTitleArChange(e.target.value)}
                                placeholder="طلب التحقق من هوية العميل"
                                required
                            />
                        </Grid.Col>
                    </Grid>

                    {/* Description fields (English & Arabic) */}
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Textarea
                                label="Description (English)"
                                value={productData.description}
                                onChange={(e) => handleDescriptionChange(e.target.value)}
                                placeholder="Order KYC product to verify user identity"
                                minRows={3}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Textarea
                                label="Description (Arabic)"
                                value={productData.description_ar}
                                onChange={(e) => handleDescriptionArChange(e.target.value)}
                                placeholder="منتج طلب التحقق من هوية العميل للتحقق من هوية المستخدم"
                                minRows={3}
                            />
                        </Grid.Col>
                    </Grid>

                    {/* Items (Pages & Products) section */}
                    <Box>
                        <Group justify="space-between" mb="sm">
                            <Text size="sm" fw={500}>
                                Items (Pages & Products) *
                            </Text>
                            <Group gap="xs">
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="violet"
                                    leftSection={<IconFileTypePdf size={14} />}
                                    onClick={handleOpenPdfIndexModal}
                                >
                                    PDF Page Order ({pdfPageIndex.length})
                                </Button>
                                <Button
                                    size="xs"
                                    variant="light"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={() => modalStates.openItemSelector('page')}
                                >
                                    Add Page{' '}
                                    {availablePagesToAdd.length > 0 && `(${availablePagesToAdd.length})`}
                                </Button>
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="green"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={() => modalStates.openItemSelector('product')}
                                >
                                    Add Product{' '}
                                    {availableProductsToAdd.length > 0 &&
                                        `(${availableProductsToAdd.length})`}
                                </Button>
                            </Group>
                        </Group>

                        <Text size="xs" c="dimmed" mb="sm">
                            Drag the grip handle to reorder items, or use up/down arrows. Items will be
                            displayed in this order.
                        </Text>

                        {/* Empty state or item list */}
                        {selectedItems.length === 0 ? (
                            <EmptyItemsState />
                        ) : (
                            <div>
                                {selectedItems.map((item, index) => (
                                    <Box key={`${item.type}-${item.id}-${index}`} mb="xs">
                                        {/* Main item card with drag and drop */}
                                        <Paper
                                            p="sm"
                                            withBorder
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                            style={{ cursor: 'move' }}
                                        >
                                            <Group justify="space-between">
                                                <Group>
                                                    <ThemeIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        color="gray"
                                                        style={{ cursor: 'grab' }}
                                                    >
                                                        <IconGripVertical size={14} />
                                                    </ThemeIcon>
                                                    <div>
                                                        <Group gap="xs">
                                                            <Text size="sm" fw={500}>
                                                                {index + 1}. {item.title}
                                                            </Text>
                                                            <ItemBadge type={item.type} />
                                                        </Group>
                                                        <Text size="xs" c="dimmed">
                                                            {item.title_ar} ({item.id})
                                                        </Text>
                                                        {item.type === 'product' && item.description && (
                                                            <Text size="xs" c="dimmed" lineClamp={1}>
                                                                {item.description}
                                                            </Text>
                                                        )}
                                                        {item.type === 'page' &&
                                                            getHiddenSectionsForPage(item.id, index).length >
                                                            0 && (
                                                                <Text size="xs" c="red.7">
                                                                    {getHiddenSectionsForPage(item.id, index).length}{' '}
                                                                    section(s) hidden
                                                                </Text>
                                                            )}
                                                    </div>
                                                </Group>
                                                <Group gap="xs">
                                                    {item.type !== 'product' && (
                                                        <>
                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="blue"
                                                                leftSection={<IconEdit size={12} />}
                                                                onClick={() => handleEditPage(item.id)}
                                                            >
                                                                Edit page
                                                            </Button>

                                                            <PdfIndexButton
                                                                pageId={item.id}
                                                                pdfPageIndex={pdfPageIndex}
                                                                onClick={handleOpenPdfIndexModal}
                                                            />

                                                            <PageVariablesButton
                                                                pageVariables={item.pageVariables}
                                                                onClick={() => handleOpenPageVariablesEditor(
                                                                    item.id,
                                                                    index,
                                                                    item.pageVariables || {},
                                                                    false
                                                                )}
                                                            />
                                                        </>
                                                    )}

                                                    {item.type === 'page' && (
                                                        <Switch
                                                            size="sm"
                                                            label="Separate Page"
                                                            checked={item.isSeparatePage || false}
                                                            onChange={(e) => {
                                                                selectedItemsHandlers.setItemProp(index, 'isSeparatePage', e.currentTarget.checked);
                                                                if (!e.currentTarget.checked) {
                                                                    selectedItemsHandlers.setItemProp(index, 'hideHeaderFooter', false);
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    {item.type === 'page' && item.isSeparatePage && (
                                                        <Switch
                                                            size="sm"
                                                            label="Hide Main Header/Footer"
                                                            checked={item.hideHeaderFooter || false}
                                                            onChange={(e) =>
                                                                selectedItemsHandlers.setItemProp(
                                                                    index,
                                                                    'hideHeaderFooter',
                                                                    e.currentTarget.checked
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {item.type === 'page' && (
                                                        <Switch
                                                            size="sm"
                                                            label="Skip KYC"
                                                            checked={item.is_skip_kyc || false}
                                                            onChange={(e) =>
                                                                selectedItemsHandlers.setItemProp(
                                                                    index,
                                                                    'is_skip_kyc',
                                                                    e.currentTarget.checked
                                                                )
                                                            }
                                                        />
                                                    )}

                                                    <Button
                                                        size="xs"
                                                        variant="light"
                                                        color={
                                                            item.product_title || item.product_title_ar ||
                                                                item.product_description || item.product_description_ar
                                                                ? 'orange'   // active override
                                                                : 'yellow'   // no override
                                                        }
                                                        leftSection={<IconEdit size={14} />}
                                                        onClick={() => handleOpenConfigModal(index)}
                                                    >
                                                        Configuration
                                                        {(item.product_title || item.product_title_ar) && ' ✓'}
                                                    </Button>
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={() =>
                                                            index > 0 && handleReorderItem(index, index - 1)
                                                        }
                                                        disabled={index === 0}
                                                        aria-label="Move up"
                                                    >
                                                        ↑
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={() =>
                                                            index < selectedItems.length - 1 &&
                                                            handleReorderItem(index, index + 1)
                                                        }
                                                        disabled={index === selectedItems.length - 1}
                                                        aria-label="Move down"
                                                    >
                                                        ↓
                                                    </ActionIcon>
                                                    <CloseButton
                                                        size="sm"
                                                        onClick={() => handleRemoveItem(index)}
                                                        aria-label="Remove item"
                                                    />
                                                </Group>
                                            </Group>
                                        </Paper>

                                        {/* Child pages for products */}
                                        {item.type === 'product' &&
                                            item.childPages &&
                                            item.childPages.length > 0 && (
                                                <Box ml={40} mt="xs">
                                                    {item.childPages.map((childPage, childIndex) => {
                                                        const overrideId = getOverrideForPage(childPage.id, index);
                                                        const overridePage = overrideId
                                                            ? availablePages.find((p) => p.id === overrideId)
                                                            : null;
                                                        const hiddenSections = getHiddenSectionsForPage(
                                                            childPage.id,
                                                            index
                                                        );

                                                        return (
                                                            <Box
                                                                key={`child-${childPage.id}-${childIndex}`}
                                                                mb="xs"
                                                            >
                                                                <Paper
                                                                    p="xs"
                                                                    withBorder
                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                >
                                                                    <Group justify="space-between">
                                                                        <Group gap="xs">
                                                                            <Text size="xs" c="dimmed">
                                                                                └─
                                                                            </Text>
                                                                            <div>
                                                                                <Group gap="xs">
                                                                                    <Text size="xs" fw={500}>
                                                                                        {childPage.title}
                                                                                    </Text>
                                                                                    <ItemBadge
                                                                                        type="page"
                                                                                        variant="light"
                                                                                    />
                                                                                </Group>
                                                                                <Text size="xs" c="dimmed">
                                                                                    {childPage.title_ar} ({childPage.id})
                                                                                </Text>
                                                                                {hiddenSections.length > 0 && (
                                                                                    <Text size="xs" c="red.7">
                                                                                        {hiddenSections.length} section(s)
                                                                                        hidden
                                                                                    </Text>
                                                                                )}
                                                                            </div>
                                                                        </Group>
                                                                        <Group gap="xs">
                                                                            <Button
                                                                                size="xs"
                                                                                variant="light"
                                                                                color="blue"
                                                                                leftSection={<IconEdit size={12} />}
                                                                                onClick={() => handleEditPage(childPage.id)}
                                                                            >
                                                                                Edit page
                                                                            </Button>
                                                                            <PdfIndexButton
                                                                                pageId={childPage.id}
                                                                                pdfPageIndex={pdfPageIndex}
                                                                                onClick={handleOpenPdfIndexModal}
                                                                            />
                                                                            <PageVariablesButton
                                                                                pageVariables={childPage.pageVariables}
                                                                                onClick={() => handleOpenPageVariablesEditor(
                                                                                    childPage.id,
                                                                                    index,
                                                                                    childPage.pageVariables || {},
                                                                                    true
                                                                                )}
                                                                            />
                                                                            <Switch
                                                                                size="sm"
                                                                                label="Skip KYC"
                                                                                checked={getSkipKycForChildPage(childPage.id, index)}
                                                                                onChange={(e) =>
                                                                                    handleUpdateChildPageSkipKyc(
                                                                                        index,
                                                                                        childPage.id,
                                                                                        e.currentTarget.checked
                                                                                    )
                                                                                }
                                                                            />
                                                                            <Button
                                                                                size="xs"
                                                                                variant="light"
                                                                                color="orange"
                                                                                leftSection={
                                                                                    <IconReplace size={12} />
                                                                                }
                                                                                onClick={() =>
                                                                                    handleOpenOverrideSelector(
                                                                                        childPage.id,
                                                                                        index
                                                                                    )
                                                                                }
                                                                            >
                                                                                {overrideId ? 'Change' : 'Override'}
                                                                            </Button>
                                                                        </Group>
                                                                    </Group>
                                                                </Paper>

                                                                {/* Override indicator */}
                                                                {overridePage && (
                                                                    <Box ml={20} mt="xs">
                                                                        <Paper
                                                                            p="xs"
                                                                            withBorder
                                                                            style={{ backgroundColor: '#fff3cd' }}
                                                                        >
                                                                            <Group justify="space-between">
                                                                                <Button
                                                                                    size="compact-xs"
                                                                                    variant="subtle"
                                                                                    color="orange"
                                                                                    leftSection={<IconEdit size={12} />}
                                                                                    onClick={() => handleEditPage(overridePage.id)}
                                                                                >
                                                                                    ➜ Override with: {overridePage.title}
                                                                                </Button>
                                                                                <CloseButton
                                                                                    size="xs"
                                                                                    onClick={() =>
                                                                                        handleRemoveOverride(
                                                                                            childPage.id,
                                                                                            index
                                                                                        )
                                                                                    }
                                                                                    aria-label="Remove override"
                                                                                />
                                                                            </Group>
                                                                        </Paper>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                            </div>
                        )}
                    </Box>

                    {/* Form action buttons */}
                    <Group justify="center" gap="md" mt="xl">
                        <Button variant="default" onClick={() => navigate('/kyc_configuration')}>
                            Cancel
                        </Button>
                        <Button leftSection={<IconCheck />} onClick={handleSave} loading={isSaving}>
                            {isEditMode ? 'Update Product' : 'Create Product'}
                        </Button>
                    </Group>
                </Stack>
            </Card>

            {/* Item Selector Modal (Pages/Products) */}
            <Modal
                opened={modalStates.showItemSelector}
                onClose={modalStates.closeItemSelector}
                title={modalStates.selectorType === 'page' ? 'Add KYC Page' : 'Add KYC Product'}
                size="md"
            >
                <Stack gap="sm">
                    {modalStates.selectorType === 'page' ? (
                        availablePagesToAdd.length === 0 ? (
                            <Text size="sm" c="dimmed" ta="center">
                                All available pages have been added.
                            </Text>
                        ) : (
                            availablePagesToAdd.map((page) => (
                                <Paper
                                    key={page.id}
                                    p="sm"
                                    withBorder
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleAddPage(page.id)}
                                >
                                    <Group justify="space-between">
                                        <div>
                                            <Text size="sm" fw={500}>
                                                {page.title}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                                {page.title_ar} ({page.id})
                                            </Text>
                                        </div>
                                        <ItemBadge type="page" size="sm" />
                                    </Group>
                                </Paper>
                            ))
                        )
                    ) : availableProductsToAdd.length === 0 ? (
                        <Text size="sm" c="dimmed" ta="center">
                            All available products have been added.
                        </Text>
                    ) : (
                        availableProductsToAdd.map((product) => (
                            <Paper
                                key={product.id}
                                p="sm"
                                withBorder
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAddProduct(product.id)}
                            >
                                <Group justify="space-between">
                                    <div>
                                        <Text size="sm" fw={500}>
                                            {product.title}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                            {product.title_ar} ({product.id})
                                        </Text>
                                        {product.description && (
                                            <Text size="xs" c="dimmed" lineClamp={1}>
                                                {product.description}
                                            </Text>
                                        )}
                                    </div>
                                    <ItemBadge type="product" size="sm" />
                                </Group>
                            </Paper>
                        ))
                    )}
                </Stack>
            </Modal>

            {/* Override Page Selector Modal */}
            <Modal
                opened={modalStates.showOverrideSelector}
                onClose={() => {
                    modalStates.closeOverrideSelector();
                    setSelectedPageForOverride(null);
                }}
                title="Select Override Page"
                size="md"
            >
                <Stack gap="sm">
                    <Text size="sm" c="dimmed" mb="sm">
                        Select a page to override "
                        {selectedPageForOverride &&
                            availablePages.find((p) => p.id === selectedPageForOverride.pageId)?.title}
                        "
                    </Text>
                    {availablePages.map((page) => {
                        const isCurrent =
                            selectedPageForOverride &&
                            getOverrideForPage(
                                selectedPageForOverride.pageId,
                                selectedPageForOverride.parentIndex
                            ) === page.id;
                        return (
                            <Paper
                                key={page.id}
                                p="sm"
                                withBorder
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: isCurrent ? '#e3f2fd' : undefined,
                                }}
                                onClick={() => handleOverridePage(page.id)}
                            >
                                <Group justify="space-between">
                                    <div>
                                        <Text size="sm" fw={500}>
                                            {page.title}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                            {page.title_ar} ({page.id})
                                        </Text>
                                    </div>
                                    {isCurrent && (
                                        <Badge size="sm" color="orange">
                                            Current
                                        </Badge>
                                    )}
                                </Group>
                            </Paper>
                        );
                    })}
                </Stack>
            </Modal>
        </main>
    );
};

export default AddProductForm;


const readImageAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
            reject(new Error('File must be an image'));
            return;
        }
        if (file.size > 200 * 1024) {
            reject(new Error(`Must be under 200KB (current: ${(file.size / 1024).toFixed(1)}KB)`));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
};

/**
 * Image upload field with base64 conversion and 200kb validation
 */
const ImageUploadField: React.FC<{
    label: string;
    value?: string;
    onChange: (base64: string | undefined) => void;
}> = React.memo(({ label, value, onChange }) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const base64 = await readImageAsBase64(file);
            onChange(base64);
        } catch (err: any) {
            showNotification({ color: 'red', title: 'Invalid Image', message: err.message, autoClose: 4000 });
        }
        e.target.value = '';
    };

    return (
        <Box>
            <Text size="xs" fw={500} mb={4}>{label}</Text>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            {value ? (
                <Group gap="xs" align="flex-start">
                    <Box
                        style={{
                            width: 150,
                            height: 100,
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}
                    >
                        <img src={value} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                    <Stack gap={4}>
                        <Button size="xs" variant="light" onClick={() => inputRef.current?.click()}>
                            Replace
                        </Button>
                        <Button size="xs" variant="subtle" color="red" onClick={() => onChange(undefined)}>
                            Remove
                        </Button>
                    </Stack>
                </Group>
            ) : (
                <Button
                    size="xs"
                    variant="light"
                    color="gray"
                    leftSection={<IconPlus size={12} />}
                    onClick={() => inputRef.current?.click()}
                >
                    Upload (max 200KB)
                </Button>
            )}
        </Box>
    );
});
